import { CellContext } from '@tanstack/react-table';
import { EditIcon, DeleteIcon, BenefitSetIcon } from '../../../../../lib/icons';
import { ActionsMenuCell, ActionMenuItem } from '../../shared/actions-menu-cell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationDialog } from '../../../compound';
import { useState } from 'react';
import { useDeleteBenefitSet } from '../../../../../queries/company/mutations';
import { BenefitSet } from '@pxtra-client-lib-legacy/gen';
import { useCreateBenefitSet } from '../../../../../pages/actions/createBenefitSet/hooks/useCreateBenefitSet';
import { useGetBenefitSet } from '../../../../../queries/benefit/benefitSet';

export function ActionsCellBenefitSets({
  info
}: Readonly<{ info: CellContext<BenefitSet, unknown> }>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { newBenefitSet } = useCreateBenefitSet();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { mutate: deleteBenefitSet } = useDeleteBenefitSet({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['benefitSets'] });
      enqueueSnackbar(t('common:DELETE_SUCCESS'), {
        variant: 'detailedSuccess',
        details: t('benefitSet:deletedSet', { benefitSet: info.row.original.name })
      });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(
        t('employee:ERROR_DELETING_EMPLOYEE', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const { data: benefitSet } = useGetBenefitSet(info.row.original.id);

  const handleClickEdit = (mode: 'edit' | 'assign') => {
    newBenefitSet.loadBenefitSet(benefitSet);

    if (benefitSet?.enabled_benefits?.length === 0) {
      newBenefitSet.assignmentContext = 'emptySet';
    }
    if (mode === 'edit') {
      navigate('/actions/benefit-set/edit_benefit_set/1');
    } else if (mode === 'assign') {
      navigate('/actions/benefit-set/assign_benefit_set_to_employees/1');
    }
  };

  const menuItems: ActionMenuItem[] = [
    {
      color: 'primary.main',
      icon: <EditIcon height={28} width={28} />,
      label: t('benefitSet:viewAndEdit'),
      onClick: () => handleClickEdit('edit'),
      hoverBgColor: 'grey.50'
    },
    {
      color: 'primary.main',
      icon: <BenefitSetIcon />,
      label: t('benefitSet:assign'),
      onClick: () => handleClickEdit('assign'),
      hoverBgColor: 'grey.50'
    },
    {
      icon: <DeleteIcon />,
      label: t('common:DELETE'),
      onClick: () => setShowDeleteDialog(true),
      color: info.row.original.employee_count == 0 ? 'error.main' : 'primary.main',
      hoverColor: 'error.dark',
      hoverBgColor: 'error.lightest',
      hasDivider: true,
      tooltipText: t('benefitSet:disabledDelete'),
      disabled: info.row.original.employee_count != 0
    }
  ];

  return (
    <>
      <ActionsMenuCell items={menuItems} />
      <ConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          deleteBenefitSet({ id: info.row.original.id });
        }}
        title={t('benefitSet:delete')}
        contentText={t('confirm_dialogs:DELETE_CONTENT_benefitSet', {
          benefitSet: info.row.original.name
        })}
        variant="delete"
      />
    </>
  );
}
